import {
  useState,
  useEffect,
  useContext,
  Suspense,
  createContext,
} from "react";
import { Outlet, useLocation } from "react-router-dom";
import messages from "@amzn/awsui-components-console/i18n/messages/all.en";
import { applyMode, Mode } from "@amzn/awsui-global-styles";
import TopNav from "./TopNav";
import SideNav from "./SideNav";
import { Helmet } from "react-helmet-async";
import { isDarkMode } from "../utils/ThemeUtils";
import { I18nProvider } from "@amzn/awsui-components-console/i18n";
import AppLayout from "@amzn/awsui-components-console/app-layout";
import Box from "@amzn/awsui-components-console/box";
import HelpPanel from "@amzn/awsui-components-console/help-panel";
import Spinner from "@amzn/awsui-components-console/spinner";

const LOCALE = "en";
interface LayoutContextProps {
  toggleHelp: () => void;
  setHelpContent: (content: any) => void;
  darkMode: boolean;
}
const LayoutContext = createContext<LayoutContextProps>({
  toggleHelp: () => {},
  setHelpContent: (content: any) => {},
  darkMode: false,
});

/**
 * Default page layout
 */
export default function Layout({ hideTools }: { hideTools?: boolean }) {
  const [toolsOpen, setToolsOpen] = useState(false);
  const [helpContent, setHelpContent] = useState(
    <HelpPanel header={<h2>Overview</h2>}>Help content</HelpPanel>
  );
  const [navigationOpen, setNavigationOpen] = useState(true);

  const [darkMode, setDarkMode] = useState(isDarkMode());

  const location = useLocation();

  function toggleHelp() {
    setToolsOpen((prevState) => !prevState);
  }

  function enableDarkMode(newDarkMode: boolean) {
    localStorage.setItem("darkMode", "" + newDarkMode);
    setDarkMode(newDarkMode);
  }

  useEffect(() => {
    setToolsOpen(false);
  }, [location]);

  useEffect(() => {
    if (darkMode) {
      applyMode(Mode.Dark);
    } else {
      applyMode(Mode.Light);
    }
  }, [darkMode]);

  return (
    <I18nProvider locale={LOCALE} messages={[messages]}>
      <Helmet>
        <meta
          httpEquiv="Content-Security-Policy"
          content={`${import.meta.env.REACT_APP_CONTENT_SECURITY_POLICY}`}
        ></meta>
      </Helmet>
      <LayoutContext.Provider
        value={{
          toggleHelp,
          setHelpContent,
          darkMode,
        }}
      >
        <div id="h" style={{ position: "sticky", top: 0, zIndex: 1002 }}>
          <TopNav isDarkMode={isDarkMode} enableDarkMode={enableDarkMode} />
        </div>
        <AppLayout
          headerSelector="#h"
          maxContentWidth={Number.MAX_VALUE}
          navigationOpen={navigationOpen}
          onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
          navigation={<SideNav />}
          toolsOpen={toolsOpen}
          toolsHide={hideTools}
          onToolsChange={({ detail }) => setToolsOpen(detail.open)}
          tools={helpContent}
          content={
            <Suspense
              fallback={
                <Box textAlign="center" padding="xxl">
                  <Spinner />
                </Box>
              }
            >
              <Outlet />
            </Suspense>
          }
        />
      </LayoutContext.Provider>
    </I18nProvider>
  );
}

export const useLayout = () => useContext(LayoutContext);
